import { setToast } from '@/entities/toast'
import { OLMSApiService } from '@/shared/apis'
import { createAsyncThunk } from '@reduxjs/toolkit'

// Types
export type ImportTarget = 'inbox' | 'existing' | 'newDate' | 'newDay' | 'runsheet' | 'rfid'

export type ImportFormData = {
  importTarget: ImportTarget
  uploadFile: File | null
  scenarioId?: string
  dateOfOperation?: string
  dayOfWeek?: string
  scenarioName?: string
  selectedSheet?: string
  selectedVersion?: string
  depotTarget?: string
}

export type ImportResponse = {
  fileName: string
  ordersTotal: number
  ordersAssigned: number
  ordersUnassigned: number
  vehiclesUsed: number
  ungeocodedLocations: number
  messages: string[]
  hasErrors: boolean
}

// Create an async thunk for handling the upload
export const uploadImportFile = createAsyncThunk(
  'import/uploadFile',
  async (formData: ImportFormData, { rejectWithValue, dispatch }) => {
    try {
      // Create form data payload
      const payload = new FormData()

      // Add file
      if (formData.uploadFile) {
        payload.append('file', formData.uploadFile)
      }

      // Add form fields based on import target
      payload.append('importTarget', formData.importTarget)
      payload.append('selectedVersion', formData.selectedVersion || '')
      payload.append('selectedSheet', formData?.selectedSheet || '')

      if (formData.importTarget === 'existing') {
        if (formData.scenarioId) {
          payload.append('scenarioId', formData.scenarioId)
        }

        if (formData.selectedVersion) {
          payload.append('selectedVersion', formData.selectedVersion)
        }
      } else if (formData.importTarget === 'newDate') {
        if (formData.dateOfOperation) {
          payload.append('dateOfOperation', formData.dateOfOperation)
        }

        if (formData.scenarioName) {
          payload.append('scenarioName', formData.scenarioName)
        }

        if (formData.selectedVersion) {
          payload.append('selectedVersion', formData.selectedVersion)
        }

        if (formData.depotTarget) {
          payload.append('depotTarget', formData.depotTarget)
        }
      } else if (formData.importTarget === 'newDay') {
        if (formData.dayOfWeek) {
          payload.append('dayOfWeek', formData.dayOfWeek)
        }

        if (formData.scenarioName) {
          payload.append('scenarioName', formData.scenarioName)
        }

        if (formData.selectedVersion) {
          payload.append('selectedVersion', formData.selectedVersion)
        }

        if (formData.depotTarget) {
          payload.append('depotTarget', formData.depotTarget)
        }
      }

      // Upload the file to the server
      const response = await OLMSApiService.upload<ImportResponse[]>(
        '/import/upload',
        payload,
      )

      if (response && response[0]) {
        const result = response[0]

        if (!result.hasErrors) {
          dispatch(setToast({
            open: true,
            severity: 'success',
            title: 'Success',
            content: 'Upload Completed',
          }))
        }

        return result
      }
    } catch (error) {
      if (error instanceof Error) {
        return rejectWithValue(error.message)
      }

      return rejectWithValue('An error occurred during upload')
    }
  },
)